import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ErrorNotice } from '../ui/feedback';
import { PageHead } from '../ui/table';
import { compact, TimeChart } from './charts';
import { useUrlFilter } from './common';

/**
 * Statistik (Revision R3): Kennzahlen pro Minute aus `GET /api/admin/metrics`. Oben Zahlen (kein Diagramm für eine
 * einzelne Zahl), darunter je Messgröße ein eigenes Diagramm mit eigener Achse. Zeitraum und Bereich in der URL,
 * gelten für alles darunter. Aktualisiert sich jede Minute; beim Nachladen bleibt das alte Bild (abgeblendet) stehen.
 */

interface Point {
  t: string;
  requests: number;
  errors4xx: number;
  errors5xx: number;
  p50: number;
  p95: number;
  max: number;
  activeUsers: number;
}
interface Metrics {
  range: string;
  bucketMinutes: number;
  series: Point[];
  totals: { requests: number; errors4xx: number; errors5xx: number; p95: number; peakActiveUsers: number };
}

const RANGES = [
  { key: '1h', label: 'Letzte Stunde' },
  { key: '24h', label: 'Letzte 24 Stunden' },
  { key: '7d', label: 'Letzte 7 Tage' },
  { key: '30d', label: 'Letzte 30 Tage' },
];
const SCOPES = [
  { key: 'all', label: 'Alle Bereiche' },
  { key: 'v1', label: 'Mod-API' },
  { key: 'web', label: 'Web (Portal)' },
  { key: 'admin', label: 'Admin' },
];

const time = (iso: string) => new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
const day = (iso: string) => new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
const dayHour = (iso: string) => `${day(iso)} ${new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit' })} Uhr`;
const long = (iso: string) =>
  new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
/** Ab 1000 ms in Sekunden („1,7 s“ statt „1,7 Tsd. ms“). */
const ms = (v: number) =>
  v >= 1000 ? `${(v / 1000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} s` : `${v.toLocaleString('de-DE')} ms`;
const percent = (part: number, total: number) =>
  total === 0 ? '–' : `${((part / total) * 100).toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`;

export function StatsPage() {
  const [rangeParam, setRange] = useUrlFilter('range');
  const [scopeParam, setScope] = useUrlFilter('scope');
  const range = rangeParam || '24h';
  const scope = scopeParam || 'all';
  const q = useQuery({
    queryKey: ['admin', 'metrics', range, scope],
    queryFn: () => api.get<Metrics>('/admin/metrics', { range, scope }),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
  const m = q.data;
  const axisTime = range === '1h' || range === '24h' ? time : range === '7d' ? day : day;
  const tipTime = range === '7d' ? dayHour : long;
  const times = m?.series.map((p) => p.t) ?? [];
  const col = (k: keyof Omit<Point, 't'>) => m?.series.map((p) => p[k]) ?? [];
  const chart = { times, formatTime: axisTime, formatTimeLong: tipTime };

  return (
    <div className="stack">
      <PageHead title="Statistik" context="Anfragen an das Backend, pro Minute erfasst, 30 Tage aufbewahrt" />
      <div className="filters">
        <select aria-label="Zeitraum" value={range} onChange={(e) => setRange(e.target.value === '24h' ? '' : e.target.value)}>
          {RANGES.map((r) => (
            <option key={r.key} value={r.key}>
              {r.label}
            </option>
          ))}
        </select>
        <select aria-label="Bereich" value={scope} onChange={(e) => setScope(e.target.value === 'all' ? '' : e.target.value)}>
          {SCOPES.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
        {m && <span className="count">Abschnitte zu {m.bucketMinutes >= 60 ? `${m.bucketMinutes / 60} h` : `${m.bucketMinutes} min`}</span>}
      </div>
      {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      {q.isPending && <p className="muted">Lädt…</p>}
      {m && (
        <div className={q.isFetching && q.isPlaceholderData ? 'stats refetching' : 'stats'}>
          <dl className="kpis">
            <div className="kpi">
              <dt>Anfragen</dt>
              <dd>{compact(m.totals.requests)}</dd>
            </div>
            <div className="kpi">
              <dt>Fehlerquote</dt>
              <dd>{percent(m.totals.errors4xx + m.totals.errors5xx, m.totals.requests)}</dd>
            </div>
            <div className="kpi">
              <dt>davon Serverfehler (5xx)</dt>
              <dd>{compact(m.totals.errors5xx)}</dd>
            </div>
            <div className="kpi">
              <dt>Antwortzeit p95 (langsamste Minute)</dt>
              <dd>{ms(m.totals.p95)}</dd>
            </div>
            <div className="kpi">
              <dt>Aktive Spieler (Spitze pro Minute)</dt>
              <dd>{compact(m.totals.peakActiveUsers)}</dd>
            </div>
          </dl>
          <div className="charts">
            <TimeChart
              {...chart}
              title="Anfragen"
              kind="bar"
              format={compact}
              series={[{ key: 'req', label: 'Anfragen', color: 'var(--series-1)', values: col('requests') }]}
            />
            <TimeChart
              {...chart}
              title="Fehler"
              kind="line"
              format={compact}
              series={[
                { key: '4xx', label: 'Anfrage abgelehnt (4xx)', color: 'var(--series-1)', values: col('errors4xx') },
                { key: '5xx', label: 'Serverfehler (5xx)', color: 'var(--series-2)', values: col('errors5xx') },
              ]}
            />
            <TimeChart
              {...chart}
              title="Antwortzeit"
              kind="line"
              format={ms}
              series={[
                { key: 'p50', label: 'Median (p50)', color: 'var(--series-1)', values: col('p50') },
                { key: 'p95', label: '95 % schneller als (p95)', color: 'var(--series-2)', values: col('p95') },
              ]}
            />
            <TimeChart
              {...chart}
              title="Aktive Spieler pro Minute"
              kind="line"
              format={compact}
              series={[{ key: 'users', label: 'Aktive Spieler', color: 'var(--series-1)', values: col('activeUsers') }]}
            />
          </div>
        </div>
      )}
    </div>
  );
}
